import React, { useEffect, useState } from "react";
import { Dropdown, Typography, Menu } from "antd";
import { DownOutlined, CheckOutlined } from "@ant-design/icons";
import { usePermissions } from "./PermissionsProvider";
import "./ModelInput.css";
import MetaLogo from "./assets/meta_primary.svg";

interface ModelInputProps {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  companyId?: number;
  showEmptyOption?: boolean;
  intentGroupId?: number;
  modelType?: "all" | "server" | "client";
  mode?: "multiple" | "tags" | undefined;
}

const ModelInput: React.FC<ModelInputProps> = ({
  value,
  onChange,
  placeholder = "Select a model",
  label = "Model",
  disabled = false,
  style = {},
  companyId = -1,
  showEmptyOption = true,
  intentGroupId,
  modelType = "all",
  mode,
}) => {
  const { apiKey } = usePermissions();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [visible, setVisible] = useState(false);

  // Define the models with their descriptions
  const availableModels = [
    {
      id: "maitai:predibase-llama-3.1-8b-instruct",
      label: "Vanilla Llama 3.1 8B",
      description: "Base model on slow GPU-based provider"
    },
    {
      id: "maitai:red_robin",
      label: (<span style={{ display: 'flex', alignItems: 'center' }}>Maitai Groq LoRA <img src={MetaLogo} alt="Meta" style={{ height: '24px', marginLeft: '4px' }} /></span>),
      description: "Fast and accurate purpose-built model"
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get display text for selected models
  const getSelectedModelsDisplay = () => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return placeholder;
    }
    const selectedModels = Array.isArray(value) ? value : [value];
    if (selectedModels.length === 1) {
      const selectedModel = availableModels.find(model => model.id === selectedModels[0]);
      return selectedModel?.id === "maitai:red_robin" ? "Maitai Groq LoRA" : (selectedModel?.label || selectedModels[0]);
    }
    const firstModel = availableModels.find(model => model.id === selectedModels[0]);
    const baseLabel = firstModel?.id === "maitai:red_robin" ? "Maitai Groq LoRA" : (firstModel?.label || selectedModels[0]);
    return `${baseLabel} +${selectedModels.length - 1}`;
  };

  // Create a basic menu without the wrapper
  const menuItems = (
    <Menu 
      selectable={false}
      onClick={(e) => {
        e.domEvent?.stopPropagation();
        e.domEvent?.preventDefault();
      }}
    >
      {availableModels.map((model) => {
        const isSelected = Array.isArray(value) 
          ? value.includes(model.id)
          : value === model.id;

        return (
          <Menu.Item 
            key={model.id}
            onClick={(e) => {
              e.domEvent?.stopPropagation();
              e.domEvent?.preventDefault();
              
              if (mode === "multiple") {
                const currentValue = Array.isArray(value) ? value : [];
                const isSelected = currentValue.includes(model.id);
                const newValue = isSelected
                  ? currentValue.filter(v => v !== model.id)
                  : [...currentValue, model.id];
                onChange?.(newValue);
                // Don't close the dropdown for multiple selection
                return false;
              } else {
                onChange?.(model.id);
                // Only close dropdown in single selection mode
                setVisible(false);
              }
            }}
          >
            <div className="model-item-content">
              <div className="model-item-title">
                {model.label}
              </div>
              <div className="model-item-description">
                {model.description}
              </div>
            </div>
            {isSelected && (
              <CheckOutlined className="model-item-check" />
            )}
          </Menu.Item>
        );
      })}
    </Menu>
  );

  const handleVisibleChange = (flag: boolean) => {
    if (!disabled) {
      setVisible(flag);
    }
  };

  return (
    <div style={{
      ...style, 
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box"
    }}>
      {label && <Typography.Text strong style={{fontSize: isMobile ? "12px" : "14px"}}>{label}</Typography.Text>}
      <Dropdown 
        overlay={menuItems}
        open={visible}
        onOpenChange={(open, info) => {
          // Only allow closing by explicit button click
          if (!open && info?.source === 'menu' && mode === 'multiple') {
            return;
          }
          handleVisibleChange(open);
        }}
        trigger={['click']}
        disabled={disabled}
        destroyPopupOnHide={false}
        dropdownRender={(menu) => (
          <div className="model-menu">
            <div className="model-menu-header">Model</div>
            {menu}
          </div>
        )}
      >
        <button 
          className="model-selector-button"
          style={{ marginTop: label ? "8px" : 0 }}
          onClick={(e) => {
            e.stopPropagation();
            setVisible(!visible);
          }}
        >
          <span className="model-name">{getSelectedModelsDisplay()}</span>
          <DownOutlined className="arrow-icon" />
        </button>
      </Dropdown>
    </div>
  );
};

export default ModelInput;
